import { useSettings } from '@wix/tpa-settings/react';
import {
  type TFunction,
  useEnvironment,
  useExperiments,
  useTranslation,
} from '@wix/yoshi-flow-editor';
import type { FC } from 'react';
import React, { useState } from 'react';
import {
  Divider,
  HoverStyles,
  TextButton,
  TextButtonPriority,
} from 'wix-ui-tpa/cssVars';

import { ABOUT_SECTION_TITLE_ID } from '../../../../constants';
import {
  useAppSettings,
  useCanEditMember,
  useMember,
} from '../../../../contexts/widgets';
import {
  DataHook,
  Experiment,
  SettingsSection,
  SettingsTab,
} from '../../../../types';
import settingsParams from '../../settingsParams';
import SectionTitle from '../common/SectionTitle';
import { About, OldAbout } from './About';
import { classes } from './Description.st.css';
import { isAboutEmpty, useAboutRendered, useNumbersVisibility } from './hooks';
import StatisticalUnit from './StatisticalUnit';

const getLikesTitle = (t: TFunction, count: number) => {
  return t('profile-stats-likes-received-icu', {
    count,
  });
};

const getCommentsTitle = (t: TFunction, count: number) => {
  return t('profile-stats-comments-icu', {
    count,
  });
};

const getTopCommentsTitle = (t: TFunction, count: number) => {
  return t('profile-stats-top-comments-icu', {
    count,
  });
};

export const Description: FC = () => {
  const settings = useSettings();
  const { t } = useTranslation();
  const { isEditor, isPreview, isMobile } = useEnvironment();
  const { numbers, about } = useMember();
  const { canEditMember } = useCanEditMember();
  const { shouldRenderAbout } = useAboutRendered();
  const numbersVisibility = useNumbersVisibility();
  const { experiments } = useExperiments();
  const appSettings = useAppSettings();

  const [isEditMode, setIsEditMode] = useState(false);

  const shouldRenderStats = Object.values(numbersVisibility).some(Boolean);
  const isAccessibleRCEEnabled = experiments.enabled(
    Experiment.EnableAccessibleRCE,
  );

  const isEmptyState = isEditor ? true : isAboutEmpty(about);
  const shouldAllowEdit = canEditMember && (!isPreview || !isMobile);

  const isSectionsAboutDefaultOpen =
    appSettings.tabOpened === SettingsTab.Sections &&
    appSettings.sectionOpened === SettingsSection.About;

  const isDesignButtonsTabOpen =
    appSettings.tabOpened === SettingsTab.Design &&
    appSettings.sectionOpened === SettingsSection.ButtonStyle;

  const shouldShowButtonsInEditor =
    isEditor && (isSectionsAboutDefaultOpen || isDesignButtonsTabOpen);

  const shouldShowEditAboutButton =
    (isAccessibleRCEEnabled &&
      shouldAllowEdit &&
      !isEmptyState &&
      !isEditMode) ||
    shouldShowButtonsInEditor;

  return (
    <>
      <div className={classes.headerRow}>
        <SectionTitle id={ABOUT_SECTION_TITLE_ID}>
          {settings.get(settingsParams.aboutSectionTitle)}
        </SectionTitle>
        {shouldShowEditAboutButton && (
          <TextButton
            data-hook={DataHook.EditAboutButton}
            className={classes.editAboutButton}
            onClick={() => setIsEditMode(true)}
            priority={TextButtonPriority.primary}
            hoverStyle={HoverStyles.underline}
            as="a"
          >
            {settings.get(settingsParams.aboutEditButtonText)}
          </TextButton>
        )}
      </div>
      {shouldRenderStats && (
        <div className={classes.statsContainer}>
          {numbersVisibility.showLikesReceivedStats && (
            <StatisticalUnit
              dataHook={DataHook.LikesReceivedStats}
              className={classes.statisticalUnit}
              tooltipText={t('profile-stats-likes-received-tooltip')}
              count={numbers.likesReceived}
              title={getLikesTitle(t, numbers.likesReceived)}
            />
          )}
          {numbersVisibility.showCommentsStats && (
            <StatisticalUnit
              dataHook={DataHook.CommentsStats}
              className={classes.statisticalUnit}
              tooltipText={t('profile-stats-comments-tooltip')}
              count={numbers.commentsReceived}
              title={getCommentsTitle(t, numbers.commentsReceived)}
            />
          )}
          {numbersVisibility.showTopCommentsStats && (
            <StatisticalUnit
              dataHook={DataHook.TopCommentsStats}
              className={classes.statisticalUnit}
              tooltipText={t('profile-stats-top-comments-tooltip')}
              count={numbers.topCommentsCount}
              title={getTopCommentsTitle(t, numbers.topCommentsCount)}
            />
          )}
        </div>
      )}
      {shouldRenderStats && shouldRenderAbout && !isMobile && (
        <Divider className={classes.divider} />
      )}
      {shouldRenderAbout &&
        (isAccessibleRCEEnabled ? (
          <About
            isEditMode={isEditMode}
            isEmptyState={isEmptyState}
            setEditMode={setIsEditMode}
            shouldShowButtonsInEditor={shouldShowButtonsInEditor}
          />
        ) : (
          <OldAbout
            shouldAllowEdit={shouldAllowEdit}
            isEmptyState={isEmptyState}
          />
        ))}
    </>
  );
};
